import { Checkbox, FormControl, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { PosTriggerType, PosTriggerTypes } from 'common/db/collections/shops/types';
import { getTypedKeys } from 'common/utils/objects';
import { TFunction } from 'i18next';
import { uniq } from 'lodash';
import React from 'react';
import { useTranslation } from 'services/localization';

type PosType = 'TEKSO' | 'CLOUD_POS';
interface Props {
	selectedTriggerTypes: PosTriggerType[];
	onTriggerTypesChange: (types: PosTriggerType[]) => void;
	posType: 'TEKSO' | 'CLOUD_POS';
}

const getTriggerTypeDescription = (triggerType: PosTriggerType, t: TFunction): string => {
	const triggerTypeDescriptionMap = {
		onOrderReceiptPrint: t('integrations.onOrderPrint', 'On order confirmation print'),
		onShopperReceiptPrint: t('integrations.onShopperPrint', 'On shopper order confirmation print'),
		externalButton: t(
			'integrations.externalButtonTrigger',
			'Using an external button in order view',
		),
	};
	return triggerTypeDescriptionMap[triggerType] ?? triggerType;
};

const posTypeNameMap: Record<PosType, string> = {
	TEKSO: 'Tekso',
	CLOUD_POS: 'Commerce Cloud',
};

const PosTriggerTypeSelection = (props: Props) => {
	const { t } = useTranslation();
	const { selectedTriggerTypes, onTriggerTypesChange } = props;

	const handleTriggerTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const triggerType = event.target.name as PosTriggerType;
		const checked = event.target.checked;
		const triggerTypes = checked
			? uniq([...selectedTriggerTypes, triggerType])
			: selectedTriggerTypes.filter((type) => type !== triggerType);
		onTriggerTypesChange(triggerTypes);
	};

	return (
		<>
			<FormControl component="fieldset" variant="standard">
				<Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
					{t('integrations.choosePosTriggerType', {
						posName: posTypeNameMap[props.posType],
						defaultValue: 'Choose how payment request is sent to {{posName}}',
					})}
				</Typography>
				<FormGroup>
					{getTypedKeys(PosTriggerTypes).map((type) => (
						<FormControlLabel
							control={
								<Checkbox
									checked={selectedTriggerTypes.includes(type)}
									onChange={handleTriggerTypeChange}
									name={type}
								/>
							}
							label={getTriggerTypeDescription(type, t)}
						/>
					))}
				</FormGroup>
			</FormControl>
		</>
	);
};

export default PosTriggerTypeSelection;
